import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      // Stay in "checking" state — AuthCallback will call refresh() after exchange
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const register = async (email, password, name) => {
    setError("");
    try {
      const ref = localStorage.getItem("aviary_ref") || "";
      const { data } = await api.post("/auth/register", { email, password, name, ref });
      localStorage.removeItem("aviary_ref");
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const googleSession = async (sessionId) => {
    setError("");
    try {
      const ref = localStorage.getItem("aviary_ref") || "";
      const { data } = await api.post("/auth/google/session", { session_id: sessionId, ref });
      localStorage.removeItem("aviary_ref");
      setUser(data);
      return data;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setUser(false);
      return null;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {/* logout is best-effort; always clear client state */}
    setUser(false);
  };

  const deleteAccount = async (confirm) => {
    setError("");
    try {
      await api.delete("/auth/me", { data: { confirm } });
      setUser(false);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  return (
    <AuthCtx.Provider value={{ user, error, login, register, googleSession, logout, deleteAccount, refresh, setError }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
